/**
 * LoginController class handles the login functionality and related tasks.
 * It manages the binding and unbinding of signals, handles login attempts,
 * and redirects users based on their roles (candidate or examiner).
 */
export class LoginController {
    /**
     * Constructor initializes the LoginController with dependencies and sets up initial state.
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $location - AngularJS $location service.
     * @param {Object} $timeout - AngularJS $timeout service.
     * @param {Object} $injector - AngularJS $injector service.
     * @param {Object} $signals - Signal handling service.
     * @param {Object} $config - Configuration service.
     * @param {Object} $objectCache - Object caching service.
     * @param {Object} UserInfoService - User information service.
     * @param {Object} $translate - Translation service.
     * @param {Object} TaskInfoService - Task information service.
     */
    constructor($scope, $location, $timeout, $injector, $signals, $config, $objectCache, UserInfoService, $translate, TaskInfoService, PWAService, AssessmentPackageManagerClient) {
        this.$scope = $scope;
        this.$location = $location;
        this.$timeout = $timeout;
        this.$injector = $injector;
        this.$signals = $signals;
        this.$config = $config;
        this.$objectCache = $objectCache;
        this.UserInfoService = UserInfoService;
        this.$translate = $translate;
        this.TaskInfoService = TaskInfoService;
        this.PWAService = PWAService;
        this.AssessmentPackageManagerClient = AssessmentPackageManagerClient;

        // Controller meta setup
        this.$scope._logIdentifier = 'LoginController';

        this._didBindSignals = false;

        this._LogBridgeService = null;
        this._AuthenticationBridgeService = null;
        this._EventLogBridgeService = null;
        this._BridgeRuntimeService = null;

        // TODO: FOR DEBUG SET TRUE
        this.$scope.isExaminer = false;

        const taskInfo = this.TaskInfoService.getTaskInfo();
        if (taskInfo.getPackageType() === "examiner") {
            this.$scope.isExaminer = true;
        }

        this.$scope.isMediaAssessment = false;
        if (taskInfo.isMediaAssessment()) {
            this.$scope.isMediaAssessment = true;
        }

        this.$scope.subject = taskInfo.getSubjectOrTitle();
        this.$scope.isCMS = false; // Ensure isCMS is defined
        this.$scope.unlockFailed = false;
        this.$scope.invalidUnlockKey = undefined;

        this.$scope.onUnlockSuccess = this.onUnlockSuccess.bind(this);
        this.$scope.onUnlockFailed = this.onUnlockFailed.bind(this);
        this.$scope.bindSignals = this.bindSignals.bind(this);
        this.$scope.unbindSignals = this.unbindSignals.bind(this);
        this.$scope.unlockTaskForCandidate = this.unlockTaskForCandidate.bind(this);
        this.$scope.unlockTaskForExaminer = this.unlockTaskForExaminer.bind(this);
        this.$scope.onLoginButtonClick = this.onLoginButtonClick.bind(this);
        this.$scope.onQuitButtonClick = this.onQuitButtonClick.bind(this);
        this.$scope.onLoginInputChange = this.onLoginInputChange.bind(this);
        // (curElementScopeName, nextElementId, maxlength) => { console.log(`${curElementScopeName} changed:`, this.$scope[curElementScopeName]); return true; }

        this._tryUnlockSuccessHandler = this.onUnlockSuccess.bind(this);
        this._tryUnlockFailedHandler = this.onUnlockFailed.bind(this);

        this.bindSignals();

        this.$scope.$on('$destroy', () => {
            this.unbindSignals();
        });

        this.$scope.$watch('schoolCode', (newVal, oldVal) => {
            console.log('schoolCode changed:', newVal);
        });

        this.$timeout(() => {
            const schoolCodeInput = document.getElementById('loginCodeBlock1');
            if (schoolCodeInput) {
                schoolCodeInput.addEventListener('input', (event) => {
                    console.log('Direct input event:', event.target.value);
                    /*this.$scope.$apply(() => {
                        this.$scope.schoolCode = event.target.value;
                    });*/
                });
            }
        });

        console.log('LoginController scope:', this.$scope);
        console.log('LoginController this:', this);
    }

    /**
     * Retrieves the LogBridgeService instance.
     * @returns {Object} LogBridgeService instance.
     */
    getLogBridgeService() {
        if (!this._LogBridgeService) {
            this._LogBridgeService = this.$injector.get('LogBridgeService');
        }
        return this._LogBridgeService;
    }

    /**
     * Retrieves the AuthenticationBridgeService instance.
     * @returns {Object} AuthenticationBridgeService instance.
     */
    getAuthenticationBridgeService() {
        if (!this._AuthenticationBridgeService) {
            this._AuthenticationBridgeService = this.$injector.get('AuthenticationBridgeService');
        }
        return this._AuthenticationBridgeService;
    }

    /**
     * Retrieves the EventLogBridgeService instance.
     * @returns {Object} EventLogBridgeService instance.
     */
    getEventLogBridgeService() {
        if (!this._EventLogBridgeService) {
            this._EventLogBridgeService = this.$injector.get('EventLogBridgeService');
        }
        return this._EventLogBridgeService;
    }

    /**
     * Retrieves the BridgeRuntimeService instance.
     * @returns {Object} BridgeRuntimeService instance.
     */
    getBridgeRuntimeService() {
        if (!this._BridgeRuntimeService) {
            this._BridgeRuntimeService = this.$injector.get('BridgeRuntimeService');
        }
        return this._BridgeRuntimeService;
    }

    /**
     * Handles the success of the unlock attempt.
     * @param {string} uuid - The UUID associated with the unlock attempt.
     */
    onUnlockSuccess(uuid) {
        this.getLogBridgeService().info(this.$scope, 'received tryUnlockSuccess with UUID ', uuid);

        const eMsg = this.$translate.instant('MODULES.LOGIN.EVENTLOG_LOGIN_SUCCESS');
        this.getEventLogBridgeService().logEvent(eMsg, 'loginSuccess', '', '', {});

        if (this.$scope.isExaminer) {
            this.$scope.unlockTaskForExaminer(uuid);
        } else {
            this.$scope.unlockTaskForCandidate(uuid);
        }

        $('#login-button').removeAttr('disabled');
    }

    /**
     * Handles the failure of the unlock attempt.
     */
    onUnlockFailed() {
        this.$scope.$apply(() => {
            this.getLogBridgeService().info(this.$scope, 'received tryUnlockFailed for unlockKey');
            this.$scope.unlockFailed = true;
            $('#login-button').removeAttr('disabled');

            const eMsg = this.$translate.instant('MODULES.LOGIN.EVENTLOG_LOGIN_FAILED');
            this.getEventLogBridgeService().logEvent(eMsg, 'loginFailed', '', '', {});

            // A candidate who never gets in produces no session.start, so without this
            // the device looks idle rather than blocked. Codes are deliberately not
            // recorded, only their shape, so a wrong-length entry is still diagnosable.
            this._failedUnlockAttempts = (this._failedUnlockAttempts || 0) + 1;
            this._recordTelemetry('login.failed', 'warning', {
                attempt: this._failedUnlockAttempts,
                isExaminer: !!this.$scope.isExaminer,
                schoolCodeLength: String(this.$scope.schoolCode || '').length,
                candidateCodeLength: String(this.$scope.candidateCode || '').length,
                unlockCodeLength: String(this.$scope.unlockCode || '').length,
            });
        });
    }

    /**
     * Records telemetry without ever throwing into the login flow.
     *
     * Resolved lazily through $injector, matching the other services in this file,
     * so the controller's DI signature stays unchanged.
     */
    _recordTelemetry(eventType, severity, data) {
        try {
            if (this.$injector.has('TelemetryBeaconService')) {
                this.$injector.get('TelemetryBeaconService').record(eventType, severity, data);
            }
        } catch (e) {
            // Telemetry must never break login.
        }
    }

    /**
     * Binds the signal events to their respective handlers.
     */
    bindSignals() {
        if (this._didBindSignals) {
            return;
        }
        this.$signals.on('tryUnlockSuccess', this._tryUnlockSuccessHandler);
        this.$signals.on('tryUnlockFailed', this._tryUnlockFailedHandler);
        this._didBindSignals = true;
    }

    /**
     * Unbinds the signal events from their respective handlers.
     */
    unbindSignals() {
        if (!this._didBindSignals) {
            return;
        }
        this.$signals.remove('tryUnlockSuccess', this._tryUnlockSuccessHandler);
        this.$signals.remove('tryUnlockFailed', this._tryUnlockFailedHandler);
        this._didBindSignals = false;
    }

    /**
     * Unlocks the task for a candidate and redirects them.
     * @param {string} uuid - The UUID associated with the candidate.
     */
    unlockTaskForCandidate(uuid) {
        this.getLogBridgeService().logEnter(this.$scope, 'unlockTaskForCandidate');

        this.$scope.loginFailed = false;

        let redirectUrl = '/user/' + uuid;
        if (this.$scope.isMediaAssessment) {
            const taskUUID = this.TaskInfoService.getTaskInfo().data.tasks[0].uuid;
            redirectUrl = (angular.isDefined(taskUUID) && angular.isUUID(taskUUID)) ? '/overview/' + taskUUID : '/instructions/' + uuid;
        }

        this.$scope.$apply(() => {
            this.getLogBridgeService().info(this.$scope, 'redirecting to ', redirectUrl);
            this.$location.path(redirectUrl);
        });

        this.getLogBridgeService().logExit(this.$scope, 'unlockTaskForCandidate');
    }

    /**
     * Unlocks the task for an examiner and redirects them.
     * @param {string} uuid - The UUID associated with the examiner.
     */
    unlockTaskForExaminer(uuid) {
        this.getLogBridgeService().logEnter(this.$scope, 'unlockTaskForExaminer');

        this.$scope.loginFailed = false;

        const redirectUrl = '/instructions/' + uuid;
        this.$scope.$apply(() => {
            this.getLogBridgeService().info(this.$scope, 'redirecting to ', redirectUrl);
            this.$location.path(redirectUrl);
        });

        this.getLogBridgeService().logExit(this.$scope, 'unlockTaskForExaminer');
    }

    /**
     * Handles the login button click event.
     */
    onLoginButtonClick() {
        const button = $('#login-button');

        if (!$(button).attr('disabled')) {
            $(button).attr('disabled', 'disabled');
            this.onLogin();
        }
    }

    /**
     * Handles the quit button click event.
     */
    onQuitButtonClick() {
        this.getBridgeRuntimeService().quit(false);
    }

    /**
     * Handles the login input change event.
     * @param {string} curElementScopeName - The current element's scope name.
     * @param {string} nextElementId - The ID of the next element to focus.
     * @param {number} maxlength - The maximum length of the input.
     */
    onLoginInputChange(curElementScopeName, nextElementId, maxlength) {
        console.log(`${curElementScopeName} changed:`, this.$scope[curElementScopeName]);
        
        if (
            typeof(curElementScopeName) === 'string' &&
            typeof(nextElementId) === 'string' &&
            this.$scope[curElementScopeName] &&
            this.$scope[curElementScopeName].length == maxlength
        ) {
            window.setTimeout(() => {
                $('#' + nextElementId).focus();
            }, 1);
        }
    }

    /**
     * Ensure the service worker has the assessment loader initialized before
     * tryUnlock is sent. After Chrome terminates an idle SW the lazy bridges
     * (auth/responseData/screenRecording) are gone until a loadAssessment runs
     * again — without this guard, tryUnlock arrives at a router with no auth
     * bridge and the login hangs silently.
     */
    async _ensureLoaderReady() {
        let status;
        try {
            status = await this.PWAService.checkServiceWorkerStatus();
        } catch (e) {
            console.warn('LoginController: SW status check failed, proceeding with unlock', e);
            return true;
        }
        if (status.loaderInitialized) {
            return true;
        }
        const metadata = this.PWAService.getCurrentMetadata();
        if (!metadata || metadata.id == null) {
            console.error('LoginController: Loader not initialized and no current assessment metadata to reload from');
            this.onUnlockFailed();
            return false;
        }
        console.log('LoginController: Loader not initialized, reloading assessment', metadata.id);
        try {
            await this.AssessmentPackageManagerClient.loadAssessment(metadata.id);
        } catch (e) {
            console.error('LoginController: Failed to reload assessment before unlock', e);
            this.onUnlockFailed();
            return false;
        }
        return true;
    }

    /**
     * Handles the login process.
     */
    onLogin() {
        let delay = 0;

        if (this.$scope.unlockFailed) {
            delay = 500;
        }

        this.$scope.unlockFailed = false;

        this.$timeout(async () => {
            console.log('Login attempt with:', {
                schoolCode: this.$scope.schoolCode,
                candidateCode: this.$scope.candidateCode,
                unlockCode: this.$scope.unlockCode
            });

            const ready = await this._ensureLoaderReady();
            if (!ready) {
                return;
            }

            if (this.$scope.isExaminer) {
                this.getAuthenticationBridgeService().tryUnlock("0", "0", this.$scope.unlockCode);
            } else {
                this.getAuthenticationBridgeService().tryUnlock(this.$scope.schoolCode, this.$scope.candidateCode, this.$scope.unlockCode);
            }
        }, delay);
    }

}

export default function registerLoginController(assessmentFrontendControllers) {
    assessmentFrontendControllers
    .controller('LoginController',
        ['$scope', '$location', '$timeout', '$injector', '$signals', '$config', '$objectCache', 'UserInfoService', '$translate', 'TaskInfoService', 'PWAService', 'AssessmentPackageManagerClient', LoginController]);
}